// Defaults & Workout rebuild — the right drawer behind every timeline entry
// and side-rail card. Resolves the `?artifact=<type>:<id>` deep link
// against the already-fetched artifact lists and renders a per-type detail
// body; contextual actions (execute forbearance, approve cost) ride the
// footer with the same gates the cards enforced.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Badge, Button } from "@/design-system/components/ui";
import { formatCalendarDate, formatCurrency } from "@/design-system/lib/format";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { useApproveWorkoutCost } from "@/hooks/portfolioAdmin/usePr4b";
import type { CollateralRecoveryDto } from "@/api/portfolioAdmin/pr4bTypes";
import { REPORT_REQUEST_STATUS_LABEL } from "@/api/portfolioAdmin/reportRequestsApi";
import { useDateFormatter } from "@/utils/datetime";
import {
  TIMELINE_TYPE_LABELS,
  isReportRequestOverdue,
  type CaseArtifacts,
  type TimelineEntryType,
} from "./timeline";
import type { WorkspaceAction } from "./CaseActionModals";

const fmtCurrency = (n: number | null | undefined): string =>
  n === null || n === undefined
    ? "—"
    : formatCurrency(n, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-space-3 border-b border-border-light py-space-2">
      <span className="text-caption text-gray-mid uppercase tracking-button shrink-0">
        {label}
      </span>
      <span className="text-small text-right tabular-nums">{value}</span>
    </div>
  );
}

/** PR 4b §F — threshold-aware approve gate (ported from the costs card). */
export function canApproveCost(
  amount: number,
  auth: { hasPermission: (p: string) => boolean; hasRoleCode: (r: string) => boolean },
): boolean {
  if (auth.hasRoleCode("ADMIN")) return true;
  if (amount < 5000) {
    return auth.hasRoleCode("A3") || auth.hasPermission("Enforcer.CostApprove");
  }
  if (amount < 25000) {
    return auth.hasRoleCode("E2") || auth.hasPermission("Enforcer.CostApprove");
  }
  return auth.hasRoleCode("E1");
}

/** Read-derived recovery-cost pipeline state (WorkoutCostPipelineReadService). */
const COST_PIPELINE_LABEL: Record<string, string> = {
  pending_review: "Pending B3 review",
  approved: "Approved",
  billed: "Billed",
  collecting: "Collecting",
  collected: "Collected",
  waived: "Waived",
};

function CostPipelineBadge({ status }: { status: string | null }) {
  if (!status) return <span className="text-small text-gray-mid">—</span>;
  const label = COST_PIPELINE_LABEL[status] ?? status;
  return <span className="text-small">{label}</span>;
}

const UCC_SALE_METHODS = new Set(["ucc-sale-public", "ucc-sale-private"]);
const STRICT_FORECLOSURE_METHODS = new Set([
  "strict-foreclosure-full",
  "strict-foreclosure-partial",
]);

interface ArtifactDrawerProps {
  artifacts: CaseArtifacts;
  selected: { type: TimelineEntryType; artifactId: string } | null;
  onClose: () => void;
  onLaunchAction: (action: WorkspaceAction) => void;
}

export function ArtifactDrawer({
  artifacts,
  selected,
  onClose,
  onLaunchAction,
}: ArtifactDrawerProps) {
  const auth = useAuth();
  const { date, tz } = useDateFormatter();
  const approve = useApproveWorkoutCost(artifacts.defaultCase.id);
  void tz;

  if (!selected) return null;
  const { type, artifactId } = selected;

  let title = TIMELINE_TYPE_LABELS[type];
  let body: ReactNode = (
    <p className="text-small text-gray-mid italic m-0">
      This entry is no longer available.
    </p>
  );
  let footer: ReactNode = null;

  switch (type) {
    case "event":
    case "note": {
      const e = artifacts.events.find((x) => x.id === artifactId);
      if (e) {
        title = type === "note" ? "Note" : e.eventSubtype;
        body = (
          <div className="flex flex-col">
            <Row label="Date" value={date(e.eventDate)} />
            <Row label="Category" value={e.eventCategory} />
            {e.dollarImpact !== null && (
              <Row label="Dollar impact" value={fmtCurrency(e.dollarImpact)} />
            )}
            {e.followUpDate && (
              <Row
                label="Follow-up"
                value={`${formatCalendarDate(e.followUpDate)}${e.followUpAssignedTo ? ` → ${e.followUpAssignedTo}` : ""}`}
              />
            )}
            <p className="text-small mt-space-3">{e.description}</p>
            <p className="text-caption text-gray-mid mt-space-2">
              Events are append-only — corrections are recorded as new entries.
            </p>
          </div>
        );
      }
      break;
    }
    case "cure-period": {
      const cp = artifacts.curePeriods.find((x) => x.id === artifactId);
      if (cp) {
        title = "Cure period";
        body = (
          <div className="flex flex-col">
            <Row label="Started" value={formatCalendarDate(cp.startDate)} />
            <Row label="Length" value={`${cp.curePeriodDays} days`} />
            <Row label="Ends" value={formatCalendarDate(cp.extendedEndDate ?? cp.endDate)} />
            <Row label="Status" value={cp.cureAchieved ? "Cured" : cp.status} />
            {cp.cureDate && <Row label="Cured on" value={date(cp.cureDate)} />}
          </div>
        );
      }
      break;
    }
    case "remediation-plan": {
      const p = artifacts.plans.find((x) => x.id === artifactId);
      if (p) {
        title = `Remediation plan — Level ${p.level}`;
        body = (
          <div className="flex flex-col">
            <Row label="Title" value={p.title} />
            <Row label="Status" value={p.status} />
            <Row label="Window" value={`${formatCalendarDate(p.startDate)} → ${formatCalendarDate(p.targetCompletionDate)}`} />
            <Row
              label="Borrower"
              value={p.borrowerAgreed ? "Agreed" : "Awaiting agreement"}
            />
            <Row
              label="Phoenix"
              value={p.phoenixApprovedByUserId ? "Approved" : "Awaiting approval"}
            />
            {p.completedDate && <Row label="Completed" value={date(p.completedDate)} />}
          </div>
        );
      }
      break;
    }
    case "forbearance": {
      const f = artifacts.forbearance.find((x) => x.id === artifactId);
      if (f) {
        title = "Forbearance agreement";
        body = (
          <div className="flex flex-col">
            <Row label="Type" value={f.forbearanceType} />
            <Row label="Status" value={f.status} />
            <Row
              label="Effective"
              value={`${formatCalendarDate(f.effectiveStartDate)} → ${formatCalendarDate(f.effectiveEndDate)}`}
            />
            <Row label="Amount" value={fmtCurrency(f.forbearanceAmount)} />
          </div>
        );
        const canManage =
          auth.hasPermission("Defaults.Manage") || auth.hasRoleCode("ADMIN");
        if (f.status === "proposed" && canManage) {
          footer = (
            <Button
              variant="primary"
              size="sm"
              onClick={() =>
                onLaunchAction({ kind: "execute-forbearance", agreementId: f.id })
              }
              data-testid="drawer-execute-forbearance"
            >
              Execute
            </Button>
          );
        }
      }
      break;
    }
    case "daca": {
      const d = artifacts.daca.find((x) => x.id === artifactId);
      if (d) {
        title = "DACA activation";
        const masked = `${d.bankName.slice(0, 4)}***-****`;
        body = (
          <div className="flex flex-col">
            <Row label="Bank" value={masked} />
            <Row label="Status" value={d.status} />
            <Row label="Activated" value={date(d.activationDate)} />
            <Row label="Monthly collection" value={fmtCurrency(d.monthlyCollectionAmount)} />
            <Row label="Redirected to date" value={fmtCurrency(d.totalFundsRedirected)} />
            {d.deactivationDate && (
              <Row label="Deactivated" value={date(d.deactivationDate)} />
            )}
          </div>
        );
      }
      break;
    }
    case "legal-action": {
      const l = artifacts.legalActions.find((x) => x.id === artifactId);
      if (l) {
        title = `Legal action — ${l.actionType}`;
        body = (
          <div className="flex flex-col">
            <Row label="Status" value={l.status} />
            <Row label="Initiated" value={date(l.initiatedDate)} />
            <Row label="Jurisdiction" value={l.courtJurisdiction ?? "—"} />
            <Row label="Case number" value={l.caseNumber ?? "—"} />
            <Row label="Fees to date" value={fmtCurrency(l.legalFeesTotal)} />
            <Row label="Settlement" value={fmtCurrency(l.settlementAmount)} />
            <Row label="Judgment" value={fmtCurrency(l.judgmentAmount)} />
            <p className="text-caption text-gray-mid mt-space-2">
              Enforcer:{" "}
              <a
                className="text-gold-shadow hover:underline"
                href={`/portfolio-admin/local-enforcers/${l.localEnforcerId}`}
              >
                {l.localEnforcerId.slice(0, 8).toUpperCase()}
              </a>
            </p>
          </div>
        );
      }
      break;
    }
    case "engagement": {
      const e = artifacts.engagements.find((x) => x.id === artifactId);
      if (e) {
        title = `Enforcer engagement — ${e.engagementType}`;
        const overdue =
          e.nextReportDue && new Date(e.nextReportDue).getTime() < Date.now();
        body = (
          <div className="flex flex-col">
            <Row label="Status" value={e.status} />
            <Row
              label="Window"
              value={`${formatCalendarDate(e.engagementStartDate)} → ${formatCalendarDate(e.expectedEndDate)}`}
            />
            <Row label="Fee" value={`${e.feeStructure} — ${fmtCurrency(e.feeAmount)}`} />
            <Row
              label="Next report due"
              value={e.nextReportDue ? date(e.nextReportDue) : "—"}
            />
            {overdue && (
              <div className="mt-space-2">
                <Badge status="error">Report overdue</Badge>
              </div>
            )}
            <p className="text-caption text-gray-mid mt-space-2">
              <a
                className="text-gold-shadow hover:underline"
                href={`/portfolio-admin/local-enforcers/${e.localEnforcerId}`}
              >
                View enforcer in the registry
              </a>
            </p>
          </div>
        );
      }
      break;
    }
    case "article-9": {
      const r = artifacts.collateralRecoveries.find((x) => x.id === artifactId);
      if (r) {
        title = `Article 9 — ${r.recoveryMethod}`;
        body = <Article9Body recovery={r} />;
      }
      break;
    }
    case "cost": {
      const c = artifacts.costs.find((x) => x.id === artifactId);
      if (c) {
        title = `Cost — ${c.costCategory}`;
        const isApproved = !!c.amountApprovedDate;
        body = (
          <div className="flex flex-col">
            <Row label="Amount" value={fmtCurrency(c.amount)} />
            <Row label="Incurred" value={formatCalendarDate(c.incurredDate)} />
            <Row
              label="Recoverable"
              value={c.recoverableFromBorrower ? "Yes" : "No"}
            />
            {c.recoverableFromBorrower ? (
              <>
                <Row
                  label="Pipeline"
                  value={<CostPipelineBadge status={c.pipelineStatus} />}
                />
                {c.billedAmount != null && (
                  <Row label="Billed" value={fmtCurrency(c.billedAmount)} />
                )}
                {c.recoveredAmount > 0 && (
                  <Row
                    label="Recovered"
                    value={fmtCurrency(c.recoveredAmount)}
                  />
                )}
                <Row label="Docs certified" value={c.docsCertified ? "Yes" : "No"} />
              </>
            ) : (
              <Row
                label="Approval"
                value={isApproved ? `Approved ${date(c.amountApprovedDate)}` : "Pending"}
              />
            )}
            {c.paidDate && <Row label="Paid" value={formatCalendarDate(c.paidDate)} />}
            <p className="text-caption text-gray-mid mt-space-2">
              {c.recoverableFromBorrower
                ? "Recoverable costs bill the borrower through B3's Fee review queue — approval happens there, not here."
                : "Non-recoverable costs are tracked on the case only and are not billed to the borrower."}
            </p>
          </div>
        );
        // Recoverable costs are B3-owned (fee queue); the direct threshold
        // approve is retired. Only the rare unapproved non-recoverable cost
        // keeps the legacy in-drawer approve as a safety.
        if (!isApproved && !c.recoverableFromBorrower && canApproveCost(c.amount, auth)) {
          footer = (
            <>
              {approve.isError && (
                <p className="text-caption text-status-error m-0 mr-auto" role="alert">
                  {extractErrorMessage(approve.error)}
                </p>
              )}
              <Button
                variant="primary"
                size="sm"
                onClick={() => approve.mutate(c.id)}
                disabled={approve.isPending}
                data-testid={`cost-approve-${c.id}`}
              >
                {approve.isPending ? "Approving…" : "Approve"}
              </Button>
            </>
          );
        }
      }
      break;
    }
    case "recovery": {
      const r = artifacts.recoveries.find((x) => x.id === artifactId);
      if (r) {
        title = `Recovery — ${r.recoverySource}`;
        body = (
          <div className="flex flex-col">
            <Row label="Date" value={formatCalendarDate(r.recoveryDate)} />
            <Row label="Gross" value={fmtCurrency(r.grossAmount)} />
            <Row label="Recovery costs" value={fmtCurrency(r.recoveryCosts)} />
            <Row label="Net" value={fmtCurrency(r.netAmount)} />
            <Row label="Applied to" value={r.appliedToWaterfall} />
          </div>
        );
      }
      break;
    }
    case "report-request": {
      const r = artifacts.reportRequests.find((x) => x.id === artifactId);
      if (r) {
        title = `${r.requestTypeLabel} report — ${r.periodLabel}`;
        const overdue = isReportRequestOverdue(r) && r.outstandingCount > 0;
        body = (
          <div className="flex flex-col">
            <Row
              label="Status"
              value={REPORT_REQUEST_STATUS_LABEL[r.status] ?? r.status}
            />
            {/* Wall-calendar values — formatCalendarDate, never `date()`. */}
            <Row
              label="Period"
              value={`${formatCalendarDate(r.periodStart)} → ${formatCalendarDate(r.periodEnd)}`}
            />
            <Row label="Due" value={formatCalendarDate(r.dueDate)} />
            <Row
              label="Progress"
              value={`${r.approvedCount} of ${r.itemCount} items approved`}
            />
            <Row label="Still outstanding" value={r.outstandingCount} />
            {/* An instant, so the tz-aware formatter is right here. */}
            <Row
              label="Issued"
              value={`${date(r.issuedAt)}${r.issuedByUserName ? ` by ${r.issuedByUserName}` : ""}`}
            />
            {overdue && (
              <div className="mt-space-2">
                <Badge status="error">Past due</Badge>
              </div>
            )}
            {r.completeWithExceptions && r.exceptionsNote && (
              <p className="text-caption text-gray-mid mt-space-2">
                Completed with exceptions: {r.exceptionsNote}
              </p>
            )}
            {/* A LINK, not an in-drawer review surface. Approving an item
                files a document and rolls the request forward; a second door
                onto that behind a case drawer would be a second place the
                review happens, and the two would drift. The link carries no
                per-request parameter because the Financial Reporting tab does
                not read one — a deep link the destination ignores is worse
                than none, because it looks like it worked. */}
            <p className="text-caption text-gray-mid mt-space-2">
              Items are reviewed on the loan&rsquo;s{" "}
              <Link
                className="text-gold-shadow hover:underline"
                to={`/portfolio-admin/loans/${artifacts.defaultCase.loanId}?tab=financial-ops`}
                data-testid="drawer-open-report-request"
              >
                Financial Reporting tab
              </Link>
              . This case timeline records that the request exists and where it
              has got to.
            </p>
          </div>
        );
      }
      break;
    }
    case "closure": {
      const c = artifacts.defaultCase;
      title = "Case closure";
      body = (
        <div className="flex flex-col">
          <Row label="Closed" value={c.closedDate ? date(c.closedDate) : "—"} />
          <Row label="Resolution" value={c.resolutionType ?? "—"} />
          <Row label="Total loss" value={fmtCurrency(c.totalLossAmount)} />
          <Row label="Recovery" value={fmtCurrency(c.recoveryAmount)} />
          {c.closureNarrative && (
            <p className="text-small mt-space-3">{c.closureNarrative}</p>
          )}
        </div>
      );
      break;
    }
  }

  return (
    <ResourceDrawer
      open
      onClose={onClose}
      title={title}
      subtitle={`Case ${artifacts.defaultCase.id.slice(0, 8).toUpperCase()}`}
      footer={footer}
      testId="case-artifact-drawer"
    >
      {body}
    </ResourceDrawer>
  );
}

function Article9Body({ recovery }: { recovery: CollateralRecoveryDto }) {
  const isUccSale = UCC_SALE_METHODS.has(recovery.recoveryMethod);
  const isStrict = STRICT_FORECLOSURE_METHODS.has(recovery.recoveryMethod);
  return (
    <div className="flex flex-col">
      <Row label="Collateral" value={recovery.collateralType} />
      <Row label="Status" value={recovery.status} />
      <Row label="Estimated" value={fmtCurrency(recovery.estimatedValue)} />
      <Row label="Appraised" value={fmtCurrency(recovery.appraisedValue)} />
      <Row label="Recovery" value={fmtCurrency(recovery.recoveryAmount)} />
      <Row
        label="Sold"
        value={recovery.recoveryDate ? formatCalendarDate(recovery.recoveryDate) : "—"}
      />
      {isUccSale && (
        <div
          className="mt-space-2 p-space-2 border-l-2 border-status-info bg-status-info/5 text-caption"
          data-testid="article-9-611-tracker"
        >
          <p className="m-0">
            <strong>§9-611</strong> — 10-day pre-sale disposition notice window.
          </p>
        </div>
      )}
      {isStrict && (
        <div
          className="mt-space-2 p-space-2 border-l-2 border-status-warning bg-status-warning/5 text-caption"
          data-testid="article-9-621-tracker"
        >
          <p className="m-0">
            <strong>§9-621</strong> — 20-day acceptance/objection window. Objection:{" "}
            {recovery.article9ObjectionReceived ? (
              <span className="text-status-error font-bold">received</span>
            ) : (
              "none on record"
            )}
            .
          </p>
        </div>
      )}
    </div>
  );
}
